import { Controller } from "react-hook-form";
import { HiArrowRight } from "react-icons/hi2";
import { Tooltip } from "@mantine/core";
import CustomLoader from "../../ui/CustomLoader/CustomLoader";
import CustomNumberInput from "../../ui/CustomNumberInput/CustomNumberInput";
import CustomSelect from "../../ui/CustomSelect/CustomSelect";
import CustomButton from "../../ui/CustomButton/CustomButton";
import { CustomText } from "../../ui/CustomText/CustomText";
import { PAYOUT_ACCOUNT_TYPES } from "../config/fundsTransferConfig";
import { FundsTransferValidators } from "../validations/FundsTransferValidations";
import { optionsFilterByLabel } from "../../../utils/optionsFilter";
import { exceedsBalance, formatKES } from "../../../utils/transactionutils";

function WithdrawalAmountForm({
  control,
  onNext,
  payoutAccountOptions,
  isAccountsLoading,
  isAccountsError,
  refetchAccounts,
  accountLabel,
  accountPlaceholder,
  walletBalance,
  isBalanceError,
  isChargesFetching,
  isFormLocked,
  watchedAmount,
  watchedSelectedPayoutAccountId,
}) {
  const hasAccount = !!watchedSelectedPayoutAccountId;
  const hasAmount = !!watchedAmount;
  const canProceed = !isFormLocked && hasAccount && hasAmount;

  let tooltipLabel = null;
  if (!hasAccount) tooltipLabel = "Select an account to continue";
  else if (!hasAmount) tooltipLabel = "Enter an amount to continue";

  return (
    <div className="space-y-4">
      <Controller
        name="selectedPayoutAccountId"
        control={control}
        rules={{
          validate: (v) =>
            FundsTransferValidators.selectedPayoutAccountId(v) ?? true,
        }}
        render={({ field, fieldState }) => (
          <CustomSelect
            label={accountLabel}
            withAsterisk
            radius="md"
            searchable
            lockOnSelect
            placeholder={
              isAccountsLoading ? "Loading accounts..." : accountPlaceholder
            }
            data={payoutAccountOptions}
            filter={optionsFilterByLabel}
            disabled={isFormLocked}
            rightSection={
              isAccountsLoading ? <CustomLoader color="primary" /> : undefined
            }
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            error={fieldState.error?.message}
          />
        )}
      />

      {isAccountsError && (
        <div className="flex items-center justify-between gap-2 px-1">
          <CustomText c="error" size="sm">
            Failed to load accounts.
          </CustomText>
          <CustomButton color="primary" size="xs" onClick={refetchAccounts}>
            Retry
          </CustomButton>
        </div>
      )}

      {!isAccountsError &&
        payoutAccountOptions.length === 0 &&
        !isAccountsLoading && (
          <CustomText c="error" size="sm">
            No accounts available. Please add an account first.
          </CustomText>
        )}

      <Controller
        name="amount"
        control={control}
        rules={{
          validate: {
            format: (v) => FundsTransferValidators.amount(v) ?? true,
            balance: (v) =>
              walletBalance != null && exceedsBalance(v, walletBalance)
                ? `Insufficient balance. Available: ${formatKES(walletBalance)}`
                : true,
          },
        }}
        render={({ field, fieldState }) => (
          <CustomNumberInput
            label="Amount to Withdraw (Ksh)"
            withAsterisk
            min={1}
            radius="md"
            placeholder="Enter amount"
            disabled={isFormLocked}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            error={fieldState.error?.message}
          />
        )}
      />

      {isBalanceError && (
        <p className="text-xs text-amber-600 px-1">
          Balance unavailable — real-time validation disabled.
        </p>
      )}

      <Tooltip
        label={tooltipLabel ?? ""}
        withArrow
        position="top"
        disabled={canProceed || !tooltipLabel}
      >
        <span className="block">
          <CustomButton
            color="primary"
            radius="md"
            rightSection={<HiArrowRight size={16} />}
            onClick={onNext}
            disabled={!canProceed}
            loading={isChargesFetching}
            fullWidth
          >
            Next: Review Details
          </CustomButton>
        </span>
      </Tooltip>
    </div>
  );
}

export function Step1Details({
  withdrawalType,
  control,
  onNext,
  payoutAccountOptions,
  isAccountsLoading,
  isAccountsError,
  refetchAccounts,
  walletBalance,
  isBalanceError,
  isChargesFetching,
  isFormLocked,
  watchedAmount,
  watchedSelectedPayoutAccountId,
}) {
  const sharedProps = {
    control,
    onNext,
    payoutAccountOptions,
    isAccountsLoading,
    isAccountsError,
    refetchAccounts,
    walletBalance,
    isBalanceError,
    isChargesFetching,
    isFormLocked,
    watchedAmount,
    watchedSelectedPayoutAccountId,
  };

  switch (withdrawalType) {
    case PAYOUT_ACCOUNT_TYPES.MOBILE_MONEY:
      return (
        <WithdrawalAmountForm
          {...sharedProps}
          accountLabel="Mobile Money Account"
          accountPlaceholder="Search by provider, phone number or name"
        />
      );
    case PAYOUT_ACCOUNT_TYPES.BANK:
      return (
        <WithdrawalAmountForm
          {...sharedProps}
          accountLabel="Bank Account"
          accountPlaceholder="Search by bank name, account number or account name"
        />
      );
    case PAYOUT_ACCOUNT_TYPES.WALLET:
      return (
        <WithdrawalAmountForm
          {...sharedProps}
          accountLabel="Paykit Wallet"
          accountPlaceholder="Select Paykit wallet"
        />
      );
    default:
      return null;
  }
}
